import { List } from '@/components/admin/list';
import { DataTable } from '@/components/admin/data-table';
import { SearchInput } from '@/components/admin/search-input';
import { Badge } from '@/components/ui/badge';

/**
 * **Le type de la ligne, passe en argument de type a chaque colonne.**
 *
 * `DataTable.Col` est generique et sa valeur par defaut est `RaRecord`, dont le seul champ sur
 * est `id`. Passer le type explicitement -- `<DataTable.Col<Structure>>` -- donne a `render` un
 * enregistrement TYPE, sans aucune conversion : une colonne qui lirait un champ absent du
 * contrat NE COMPILE PAS. C'est la couche contrat de `DOCTRINE.md` §4 appliquee jusqu'a la
 * cellule -- « inventer un champ : le type ne le porte pas, la compilation echoue ».
 */
type Structure = {
  id: string;
  nomInterne: string;
  creeLe: string;
  paraitSurLeSite: boolean;
  secretEmis: boolean;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ÉCRAN D'ARRIVÉE DU COMPTE QUI VOIT TOUT — F-55 ET F-56.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-55 : « **l'écran d'arrivée est asymétrique par rôle** : la liste des structures pour le
 * compte qui voit tout, l'espace de sa structure pour la personne qui l'administre. » Cet
 * écran est donc **la première moitié** de cette asymétrie, et la matrice ferme `list` à
 * l'`orgadmin` : la peau ne lui montre ni l'entrée de menu ni la page.
 *
 * F-55 encore, deux règles qui décident ce que cet écran NE porte PAS :
 * « **aucun tableau de bord, aucun chiffre en tête de surface.** Les chiffres vivent à leur
 * écran », et « **entrer dans la surface et parcourir une liste ne laissent aucune ligne de
 * trace. Seule l'ouverture d'une fiche en laisse une.** »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **QUATRE COLONNES, ET ELLES SONT CHOISIES.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `admin/CLAUDE.md` : « aucune colonne affichée par défaut **parce qu'elle existe dans le
 * modèle** : les colonnes sont choisies. **Une ressource d'administration n'est pas une
 * fenêtre sur la base.** » La structure porte quatorze colonnes en base — nom interne, six
 * colonnes d'affichage, cinq réglages, empreinte du secret, instant de création. La liste en
 * montre **quatre**, et chacune répond à une question qu'on se pose en ouvrant cet écran.
 *
 * - **le nom interne** : la seule désignation qu'un compte d'administration emploie ;
 * - **l'instant de création** : l'ordre d'entrée des structures au parc ;
 * - **paraît sur le site** : F-58, « l'écran dit si la structure paraît sur le site » — la
 *   présence est **dérivée et jamais stockée** (Q539), donc indevinable autrement ;
 * - **secret émis** : F-57, un **booléen dérivé, jamais l'empreinte**. C'est ce qui dit si
 *   la structure peut encore appeler l'API, et cas de bord 2 le rend utile : « une structure
 *   sans aucune personne pour tenir son espace […] **ses appels marchent encore** ».
 *
 * **Ni les réglages ni les colonnes d'affichage n'y sont**, alors que le rôle qui lit cet
 * écran a le droit de les voir : ils appartiennent à la fiche. Une liste qui porterait cinq
 * réglages par ligne serait la fenêtre sur la base que la règle refuse.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA RECHERCHE OUI, LE TRI NON.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Le contrat porte `recherche` sur le nom interne, et **aucun paramètre de tri**. Les
 * colonnes sont donc déclarées `disableSort` : une flèche de tri qui ne trie pas est pire
 * qu'une absence de flèche — elle se clique, et rien ne bouge.
 */
export function ListeDesStructures() {
  return (
    <List
      // La recherche est le seul filtre : le contrat n'en porte pas d'autre sur cette route.
      filters={[<SearchInput key="recherche" source="recherche" alwaysOn />]}
      // **Aucun tri par défaut n'est envoyé** : le fournisseur de données l'ignorerait, et
      // en déclarer un donnerait à croire que l'ordre est réglable.
      sort={undefined}
      perPage={20}
      // **Aucun exportateur ici.** F-66 : « **DEUX EXPORTS, ET DEUX SEULEMENT** : la liste
      // des comptes rattachés à une structure, et la liste de ses cours. **Aucun
      // troisième.** » L'exportateur générique de la peau produirait le troisième.
      exporter={false}
    >
      <DataTable>
        <DataTable.Col source="nomInterne" label="Nom interne" disableSort />
        <DataTable.Col<Structure>
          source="creeLe"
          label="Créée le"
          disableSort
          render={(structure) =>
            new Date(structure.creeLe).toLocaleDateString('fr-FR')
          }
        />
        <DataTable.Col<Structure>
          source="paraitSurLeSite"
          label="Sur le site public"
          disableSort
          render={(structure) =>
            structure.paraitSurLeSite ? (
              <Badge variant="default">paraît</Badge>
            ) : (
              // **« ne paraît pas » et non « retirée »** : aucun geste « retirer du site »
              // n'existe (F-58), et aucun état de clientèle non plus (Q539). Ce qui manque
              // est dit sur la fiche, où la liste des colonnes déclencheuses absentes vit.
              <Badge variant="outline">ne paraît pas</Badge>
            )
          }
        />
        <DataTable.Col<Structure>
          source="secretEmis"
          label="Secret d’appel"
          disableSort
          render={(structure) =>
            structure.secretEmis ? (
              <Badge variant="secondary">émis</Badge>
            ) : (
              <Badge variant="outline">révoqué</Badge>
            )
          }
        />
      </DataTable>
    </List>
  );
}
